import { useCallback, useEffect, useMemo, useState } from "react";
import { useUserAgents } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";
import { CONSOLE_GENIE_LIMIT } from "./useGenieConsole";

/**
 * localStorage key for the dashboard's last-viewed genie. Mirrors the
 * `genie-view-mode` pattern already used by My Genies — plain localStorage,
 * no extra dependency.
 */
export const SELECTED_GENIE_STORAGE_KEY = "dashboard-selected-genie";

const readStoredId = (): string | null => {
  try {
    return window.localStorage.getItem(SELECTED_GENIE_STORAGE_KEY);
  } catch {
    // Private mode / storage disabled — fall back to "first genie".
    return null;
  }
};

const writeStoredId = (genieId: string) => {
  try {
    window.localStorage.setItem(SELECTED_GENIE_STORAGE_KEY, genieId);
  } catch {
    // Non-fatal: the dashboard still works, it just won't remember.
  }
};

/**
 * Which genie the dashboard is pointed at, remembered across visits (HG-1229).
 *
 * Reads the same `useUserAgents` query the console does, so React Query
 * dedupes it into a single request. A stored genie that no longer exists (or
 * was never set) falls back to the first genie on the account.
 */
export const useSelectedGenieId = () => {
  const { data, isLoading } = useUserAgents(CONSOLE_GENIE_LIMIT);
  const genies = useMemo<Agent[]>(() => (data as Agent[] | undefined) ?? [], [data]);

  const [storedId, setStoredId] = useState<string | null>(() =>
    typeof window === "undefined" ? null : readStoredId(),
  );

  const selectedGenieId = useMemo(() => {
    if (genies.length === 0) return null;
    if (storedId && genies.some((genie) => genie.id === storedId)) return storedId;
    return genies[0].id;
  }, [genies, storedId]);

  // Persist the resolved fallback so a deleted genie doesn't keep re-resolving.
  useEffect(() => {
    if (selectedGenieId && selectedGenieId !== storedId) {
      writeStoredId(selectedGenieId);
      setStoredId(selectedGenieId);
    }
  }, [selectedGenieId, storedId]);

  const selectGenie = useCallback((genieId: string) => {
    writeStoredId(genieId);
    setStoredId(genieId);
  }, []);

  return { selectedGenieId, selectGenie, isResolvingGenieId: isLoading };
};
